// @flow

import isValid from 'date-fns/isValid';

export const MS_SECOND = 1000;
export const MS_MINUTE = MS_SECOND * 60;
export const MS_HOUR = MS_MINUTE * 60;
export const MS_DAY = MS_HOUR * 24;
export const MS_MONTH = MS_DAY * 30;
export const MS_YEAR = MS_DAY * 365;

export const timeframesFromNow = (date: number | string) => {
  const milli = new Date(date) - new Date();
  const ms = Math.abs(milli);

  return {
    year: Math.floor(ms / MS_YEAR),
    month: Math.floor((ms % MS_YEAR) / MS_MONTH),
    day: Math.floor((ms % MS_MONTH) / MS_DAY),
    hour: Math.floor((ms % MS_DAY) / MS_HOUR),
    minute: Math.floor((ms % MS_HOUR) / MS_MINUTE),
    milli,
    ms,
  };
};

/**
 * Add days to a date.
 *
 * @param {Date} date The date to be added from.
 * @param {number} daysToAdd The days to add.
 * @return {Date} The date that is "daysToAdd" days after "date".
 */
export const addDays = (date: Date, daysToAdd: number) => {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + daysToAdd);
  return newDate;
};

export const isoDateSubtract = (numDaysToSubtract: number, startDate: Date) => {
  const dateOffset = startDate.getUTCDate() - numDaysToSubtract;
  const myDate = new Date(startDate);
  myDate.setUTCDate(dateOffset);
  return myDate;
};

export const isoDateIsLater = (targetDate: Date, inflectionDate: Date) => {
  const target = targetDate.getTime();
  const inflection = inflectionDate.getTime();
  return target > inflection;
};

const dateStringIsInYYYY = (dateStr: string): boolean => /^\d{4}$/.test(dateStr);
const dateStringIsInMMDDYYYY = (dateStr: string): boolean => /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr);

export const formatBirthdayToUnixTime = (birthday: string): number => {
  if (!dateStringIsInYYYY(birthday) && !dateStringIsInMMDDYYYY(birthday)) {
    return -1;
  }
  const birthDate = new Date(birthday);
  if (!isValid(birthDate)) {
    return -1;
  }
  const isoString = birthDate.toISOString(); // YYYY-MM-DDTHH:mm:ss.sssZ
  const yyyyMmDd = isoString.substr(0, 10); // YYYY-MM-DD
  const startOfDayInUtc = new Date(yyyyMmDd);
  return startOfDayInUtc.getTime() / MS_SECOND;
};

export const isBirthdayOlderThanMinAge = (birthday: string, minAge: number): boolean => {
  if (!dateStringIsInYYYY(birthday) && !dateStringIsInMMDDYYYY(birthday)) {
    return false;
  }

  const currentDate = new Date();
  if (!isValid(currentDate)) {
    return false;
  }

  if (!dateStringIsInYYYY(birthday)) {
    // not in year
    const minBirthdate = new Date(
      // If birthday is not in year format, make sure it's before or on minAge years ago today
      currentDate.getFullYear() - minAge,
      currentDate.getMonth(),
      currentDate.getDate(),
    );
    const birthdate = new Date(birthday);
    return birthdate <= minBirthdate;
  }

  // If birthday is in year format, make sure it's (minAge + 1) years ago. We need the extra buffer year to be safe.
  const minBirthYear = currentDate.getFullYear() - minAge - 1;
  const birthYear = new Date(birthday).getFullYear();
  return birthYear <= minBirthYear;
};

/**
 * Return the UTC date with only year, month and day set.
 *
 * @param {string|number} date Date in ISO string format or milliseconds since
 * January 1, 1970, 00:00:00 UTC
 * @return {Date} UTC date.
 */
export const UTCDate = (date: string | number): Date => {
  const d = new Date(date);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * @return a string 'YYYY-MM-DD'
 */
export const formatLocalDate = (date: Date) =>
  date.getFullYear() +
  '-' +
  ('0' + (date.getMonth() + 1)).slice(-2) +
  '-' +
  ('0' + date.getDate()).slice(-2);
